import { useMarket } from "@/hooks/useMarket";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { marketPrepays, type MarketCountry } from "@/config/market";

/**
 * The kill-switch key for a market. One flag PER market, not one for both: the
 * invoicing question that put this path in the dark is specifically German
 * (GOÄ multiplier, débours pass-through, 19% VAT, reverse charge to the lab),
 * so NL can be switched on without waiting for Germany, or the other way round.
 *
 * No migration creates these. Production has no rows at all, which this hook
 * reads as off. Turning a market on means inserting its row by hand there.
 */
export const prepayFlagKey = (market: MarketCountry) => `prepay_${market}`;

/**
 * Whether this market takes payment up front: market capability AND its flag.
 *
 * The DE/NL one-off checkout is written and covered end to end, but the billing
 * structure behind it is being redesigned with the tax advisers: the lab
 * analysis is meant to pass through as a débours, with the treatment contract
 * directly between customer and lab, while Ahead invoices its platform fee
 * separately at 19% VAT. Until that is settled the path stays dark, and DE/NL
 * fall through to the ordinary booking request exactly as they do today.
 *
 * A hook rather than the inlined `flag && market` pattern used for
 * membership_v2, on purpose: this gates a MONEY path across six call sites, two
 * of which only pick copy ("no upfront payment" versus "free cancellation").
 * Missing one of those would leave a market promising a payment that never
 * happens. One place to be right.
 *
 * Defaults to FALSE, unlike membership_v2's `?? true`. A missing row, a failed
 * fetch or the first render before the query resolves must all mean "do not
 * charge", never "charge under a structure the lawyers have not signed off".
 */
export function usePrepayEnabled(): boolean {
  const { market } = useMarket();
  const { data: featureFlags } = useFeatureFlags();

  return (
    marketPrepays(market) && (featureFlags?.[prepayFlagKey(market)] ?? false)
  );
}
